import * as React from "react";

import { cn } from "@/lib/utils";

import { DialogOverlay, DialogPortal, DialogPopup } from "./dialog";

/**
 * Fade + subtle scale from centre — use `VideoDialogPopup` (not
 * `DialogPrimitiveContent`) to avoid the default slide-from-corner transition.
 *
 * Reference: home `#customers` (`components/home/customers-slider.tsx`).
 */
export const videoDialogOverlayClass = cn(
  "data-open:animate-in data-closed:animate-out",
  "data-open:fade-in-0 data-closed:fade-out-0",
  "bg-surface-inverse/80 backdrop-blur duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:animate-none"
);

export const videoDialogOverlayDarkClass = cn(
  "data-open:animate-in data-closed:animate-out",
  "data-open:fade-in-0 data-closed:fade-out-0",
  "bg-black/80 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:animate-none"
);

export const videoDialogPopupClass = cn(
  "data-open:animate-in data-closed:animate-out",
  "data-open:fade-in-0 data-closed:fade-out-0",
  "data-open:zoom-in-95 data-closed:zoom-out-95",
  "fixed top-1/2 left-1/2 z-50 max-w-none -translate-x-1/2 -translate-y-1/2",
  "origin-center duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:animate-none"
);

/** Transparent shell with inner rounded video — home `#customers` pattern. */
export const videoDialogPopupHomeClass = cn(
  videoDialogPopupClass,
  "w-[min(90vw,calc(90vh*16/9))] border-0 bg-transparent p-4 shadow-none md:p-6 lg:max-w-[80vw]"
);

/** Black rounded card shell — our-customers hero. */
export const videoDialogPopupCardClass = cn(
  videoDialogPopupClass,
  "w-[min(90vw,calc(90vh*16/9))] overflow-hidden rounded-2xl border-0 bg-black p-0 shadow-2xl"
);

/** Narrower black card shell (70vw) — financing showcase. */
export const videoDialogPopupCardCompactClass = cn(
  videoDialogPopupClass,
  "w-[min(70vw,calc(70vh*16/9))] overflow-hidden rounded-2xl border-0 bg-black p-0 shadow-2xl"
);

export const videoDialogCloseClass =
  "text-grey-0 absolute top-8 right-8 z-10 flex size-10 items-center justify-center rounded-full bg-black/50 opacity-80 backdrop-blur-sm transition-[opacity,background-color] duration-150 hover:bg-black/65 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 md:top-10 md:right-10 [&_svg]:size-4";

export const videoDialogCloseCompactClass =
  "text-grey-0 absolute top-4 right-4 rounded-full bg-black/50 px-3 py-1 text-sm";

export const videoDialogCloseIconClass =
  "bg-icon-primary [&>svg]:stroke-icon-on-dark top-10 right-10 rounded-none p-2 md:top-12 md:right-12";

const videoDialogPopupClassByVariant = {
  home: videoDialogPopupHomeClass,
  card: videoDialogPopupCardClass,
  "card-compact": videoDialogPopupCardCompactClass,
} as const;

export type VideoDialogVariant = keyof typeof videoDialogPopupClassByVariant;

type VideoDialogPopupProps = React.ComponentPropsWithoutRef<
  typeof DialogPopup
> & {
  overlayClassName?: string;
  variant?: VideoDialogVariant;
};

/** Portal + overlay + fade-in popup — drop children inside. */
export function VideoDialogPopup({
  className,
  overlayClassName,
  variant = "home",
  children,
  initialFocus = false,
  ...props
}: VideoDialogPopupProps) {
  return (
    <DialogPortal>
      <DialogOverlay
        className={cn(videoDialogOverlayClass, overlayClassName)}
      />
      <DialogPopup
        initialFocus={initialFocus}
        className={cn(videoDialogPopupClassByVariant[variant], className)}
        {...props}
      >
        {children}
      </DialogPopup>
    </DialogPortal>
  );
}
